/**
 * RUBYTILL PAY — the register-dialog pieces.
 *
 * Our own payment app on a Dejavoo P-series terminal (spec:
 * docs/agenticpos-rubytill/rubytill-pay.md). It is the THIRD route for a
 * P-series device beside SPIn USB and SPIn Cloud, which stay untouched. These
 * pieces are shared by every place a device is registered — RegisterPoiModal,
 * the order's card-processing lane (DeviceSetupPanels) and the replace wizard —
 * so the three cannot drift.
 *
 *   RtpayModeCards          "What it does at this register" — Charge only / Customer display
 *   RubyTillPayRegisterFields  TPN (optional here) + mode + Terminal PIN
 *   RtpayPairingCodePanel   the black "482 917 — type it on the terminal" panel;
 *                           polls GET rtpay every 3s until `status.paired`
 *
 * The device class is a local string on purpose: these screens must render
 * even under a dev server whose pre-bundled shared-types predates RubyTill Pay.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MonitorSmartphone, CreditCard, Check, RefreshCw } from 'lucide-react';
import { api } from '../../services/apiService';
import { RubyGemMark } from '../RubyGemMark';

export const RTPAY_CLASS = 'dejavoo_rubytill_pay';
export type RtpayRegisterMode = 'charge_only' | 'customer_display';

export const isRtpayDevice = (d: { deviceClass?: string | null } | null | undefined) =>
  String(d?.deviceClass || '') === RTPAY_CLASS;
/*
  ANDROID TAP TO PAY — the same RubyTill Pay lane on a plain Android device; only
  the card engine differs (Dejavoo's Tap to Pay SDK, NFC, instead of DvPayLite).
  Mirrors ANDROID_TAP_TO_PAY_MODEL in @platform/shared-types rubytillPay.ts —
  a local string for the same dep-cache reason as RTPAY_CLASS.
*/
export const ANDROID_TAP_TO_PAY_MODEL = 'Android Tap to Pay';
export const isTapToPayDevice = (d: {
  model?: string | null; rtpay?: { cardEngine?: string | null } | null; rtpayCardEngine?: string | null; cardEngine?: string | null;
} | null | undefined) =>
  !!d && (d.model === ANDROID_TAP_TO_PAY_MODEL || d.rtpay?.cardEngine === 'tap_to_pay'
    || d.rtpayCardEngine === 'tap_to_pay' || d.cardEngine === 'tap_to_pay');
/** What a RubyTill Pay device is called on a row: "Android Tap to Pay" or the lane's label. */
export const rtpayProductLabel = (d: Parameters<typeof isTapToPayDevice>[0], fallback = 'RubyTill Pay') =>
  isTapToPayDevice(d) ? ANDROID_TAP_TO_PAY_MODEL : fallback;
/** Tap to Pay SDK merchant code — digits only, 6–20 (the server's rule). */
export const isValidMerchantCode = (v: string) => /^\d{6,20}$/.test(v.trim());
export const isValidRtpayPin = (v: string) => /^\d{4,6}$/.test(v.trim());
export const isValidTpn = (v: string) => /^\d{12}$/.test(v.trim());

/** "482917" → "482 917" */
export const formatPairCode = (code: string) => {
  const c = String(code || '').replace(/\D/g, '');
  return c.length === 6 ? `${c.slice(0, 3)} ${c.slice(3)}` : code;
};

/** ISO string, epoch seconds or epoch ms → ms. */
export const toMs = (t: unknown): number | null => {
  if (t === null || t === undefined || t === '') return null;
  if (typeof t === 'number') return t < 1e12 ? t * 1000 : t;
  const n = Number(t);
  if (Number.isFinite(n)) return n < 1e12 ? n * 1000 : n;
  const d = Date.parse(String(t));
  return Number.isFinite(d) ? d : null;
};

// ---------------------------------------------------------------------------
// Mode cards
// ---------------------------------------------------------------------------

export const RTPAY_MODES: Array<{ id: RtpayRegisterMode; title: string; desc: string; icon: React.ReactNode }> = [
  {
    id: 'charge_only',
    title: 'Charge only',
    desc: 'Dual-screen till. The till sends the final amount; the P17 just takes the card.',
    icon: <CreditCard size={16} />,
  },
  {
    id: 'customer_display',
    title: 'Customer display',
    desc: 'Single-screen till. Tip, card and receipt happen on the P17.',
    icon: <MonitorSmartphone size={16} />,
  },
];

export const RtpayModeCards: React.FC<{
  value: RtpayRegisterMode;
  onChange: (m: RtpayRegisterMode) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <div role="radiogroup" aria-label="What it does at this register" className="grid gap-2 sm:grid-cols-2">
    {RTPAY_MODES.map((m) => {
      const on = value === m.id;
      return (
        <button
          key={m.id}
          type="button"
          role="radio"
          aria-checked={on}
          data-testid={`rtpay-mode-${m.id}`}
          disabled={disabled}
          onClick={() => onChange(m.id)}
          className={`flex min-h-[44px] items-start gap-2.5 rounded-xl border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            on ? 'border-ruby bg-[#FFF1F3]' : 'border-stone-200 bg-white hover:border-stone-300'}`}
        >
          <span className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg ${on ? 'bg-ruby text-white' : 'bg-stone-100 text-stone-600'}`}>
            {m.icon}
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold text-stone-900">{m.title}</span>
            <span className="mt-0.5 block text-[11.5px] leading-snug text-stone-600">{m.desc}</span>
          </span>
        </button>
      );
    })}
  </div>
);

// ---------------------------------------------------------------------------
// Register fields
// ---------------------------------------------------------------------------

const INPUT = 'min-h-[44px] w-full rounded-lg border bg-white px-3 font-mono text-sm text-stone-900 outline-none placeholder-stone-400 focus:border-ruby focus:ring-2 focus:ring-rose-200';

export interface RubyTillPayRegisterFieldsProps {
  mode: RtpayRegisterMode;
  onModeChange: (m: RtpayRegisterMode) => void;
  pin: string;
  onPinChange: (v: string) => void;
  /** Omit when the host form already asks for the TPN. */
  tpn?: string;
  onTpnChange?: (v: string) => void;
  disabled?: boolean;
}

export const RubyTillPayRegisterFields: React.FC<RubyTillPayRegisterFieldsProps> = ({
  mode, onModeChange, pin, onPinChange, tpn, onTpnChange, disabled,
}) => {
  const tpnBad = tpn !== undefined && !!tpn.trim() && !isValidTpn(tpn);
  const pinBad = !!pin.trim() && !isValidRtpayPin(pin);
  return (
    <div className="space-y-3" data-testid="rtpay-register-fields">
      {tpn !== undefined && onTpnChange && (
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-stone-700">iPOS Transact TPN</span>
          <input
            type="text"
            inputMode="numeric"
            value={tpn}
            disabled={disabled}
            onChange={(e) => onTpnChange(e.target.value.replace(/\D/g, '').slice(0, 12))}
            placeholder="12-digit TPN, e.g. 220926369218"
            className={`${INPUT} ${tpnBad ? 'border-red-300 bg-red-50' : 'border-stone-300'}`}
          />
          <span className="mt-1 block text-[11px] text-stone-500">12 digits. Sent to the terminal at pairing.</span>
        </label>
      )}

      <div>
        <span className="mb-1 block text-xs font-medium text-stone-700">What it does at this register</span>
        <RtpayModeCards value={mode} onChange={onModeChange} disabled={disabled} />
      </div>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-stone-700">Terminal PIN</span>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          value={pin}
          disabled={disabled}
          onChange={(e) => onPinChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="4–6 digits"
          className={`${INPUT} tracking-widest sm:max-w-[200px] ${pinBad ? 'border-red-300 bg-red-50' : 'border-stone-300'}`}
        />
        <span className="mt-1 block text-[11px] text-stone-500">For the P17 staff menu. Stored hashed; never shown again.</span>
      </label>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pairing code
// ---------------------------------------------------------------------------

export interface RtpayPairingCode { code: string; expiresAt: string | number }

const mmss = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export interface RtpayPairingCodePanelProps {
  tenantId: string;
  deviceId: string;
  /** A code just issued (by the register step) — shown at once. */
  initial?: RtpayPairingCode | null;
  /** No code yet: issue one on mount rather than waiting for a press. */
  autoStart?: boolean;
  onPaired?: () => void;
  /** Hide the panel (the host's "Done"/"Close"). */
  onClose?: () => void;
}

/**
 * THE BLACK PAIRING PANEL. A code is single use, 10 minutes, and a new one
 * replaces the old on the server — so "New code" is always safe to press.
 * Polls GET rtpay every 3s while a live code is on screen; stops on paired,
 * on expiry, and on unmount.
 */
export const RtpayPairingCodePanel: React.FC<RtpayPairingCodePanelProps> = ({
  tenantId, deviceId, initial, autoStart, onPaired, onClose,
}) => {
  const [pair, setPair] = useState<RtpayPairingCode | null>(initial || null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paired, setPaired] = useState(false);
  const onPairedRef = useRef(onPaired);
  onPairedRef.current = onPaired;

  const issue = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const res = await api.posNmi.newRtpayPairingCode(tenantId, deviceId);
      if (!res?.code) throw new Error('No code came back.');
      setPair({ code: String(res.code), expiresAt: res.expiresAt });
      setPaired(false);
    } catch (e: any) {
      setError(e?.serverMessage || e?.message || 'Could not get a pairing code.');
    } finally {
      setBusy(false);
    }
  }, [tenantId, deviceId]);

  useEffect(() => { if (!initial && autoStart) issue(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const expMs = pair ? toMs(pair.expiresAt) : null;
  const left = expMs ? expMs - now : 0;
  const live = !!pair && !paired && left > 0;

  // Countdown tick.
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);

  // Poll for the terminal redeeming the code.
  useEffect(() => {
    if (!live) return;
    let stop = false;
    const t = setInterval(async () => {
      try {
        const res = await api.posNmi.getRtpay(tenantId, deviceId);
        /* Paired BY THIS CODE: the outstanding code is gone and the pairing
           is no older than the code (a re-pair of an already-paired terminal
           must not read as done the moment polling starts). */
        const pairedAt = toMs(res?.rtpay?.pairedAt);
        const issuedAt = (expMs || 0) - 10 * 60 * 1000;
        const fresh = pairedAt === null || pairedAt >= issuedAt - 60 * 1000;
        if (!stop && res?.status?.paired && !res?.pairing && fresh) {
          setPaired(true);
          onPairedRef.current?.();
        }
      } catch { /* keep polling — a blip is not a failure */ }
    }, 3000);
    return () => { stop = true; clearInterval(t); };
  }, [live, tenantId, deviceId, expMs]);

  return (
    <div data-testid="rtpay-pairing-panel" className="overflow-hidden rounded-xl bg-stone-950 text-white">
      <div className="h-[3px] bg-ruby" />
      <div className="p-4">
        {paired ? (
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-green-600"><Check size={18} /></span>
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-semibold">Paired</p>
              <p className="text-[12px] text-stone-400">The terminal is connected to RubyTill Pay.</p>
            </div>
            {onClose && (
              <button type="button" onClick={onClose} className="min-h-[44px] rounded-lg bg-white px-4 text-[13px] font-semibold text-stone-900 hover:bg-stone-100">
                Done
              </button>
            )}
          </div>
        ) : pair && live ? (
          <>
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-400">Pairing code</p>
            <p className="mt-1 font-mono text-[34px] font-bold leading-none tracking-[0.12em]" data-testid="rtpay-pair-code">
              {formatPairCode(pair.code)}
            </p>
            <p className="mt-2 text-[12.5px] text-stone-300">
              Type it on the terminal · Expires in <span className="font-mono tabular-nums">{mmss(left)}</span> · single use
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[12.5px] text-stone-300">
              <RubyGemMark size={16} drawing color="currentColor" />
              <span>Waiting for the terminal…</span>
              <span className="ml-auto flex gap-2">
                <button type="button" onClick={issue} disabled={busy}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-stone-700 px-3 text-[12px] font-semibold text-white hover:bg-stone-800 disabled:opacity-50">
                  <RefreshCw size={13} /> New code
                </button>
                {onClose && (
                  <button type="button" onClick={onClose}
                    className="min-h-[44px] rounded-lg px-3 text-[12px] font-semibold text-stone-300 hover:text-white">
                    Close
                  </button>
                )}
              </span>
            </div>
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold">{pair ? 'That code has expired' : 'Pair the terminal'}</p>
              <p className="text-[12px] text-stone-400">
                Open RubyTill Pay on the terminal and type the 6-digit code. Codes last 10 minutes and work once.
              </p>
            </div>
            <button type="button" onClick={issue} disabled={busy} data-testid="rtpay-issue-code"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-white px-4 text-[13px] font-semibold text-stone-900 hover:bg-stone-100 disabled:opacity-60">
              {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : <RefreshCw size={14} />}
              {pair ? 'New pairing code' : 'Get pairing code'}
            </button>
            {onClose && (
              <button type="button" onClick={onClose}
                className="min-h-[44px] rounded-lg px-3 text-[12px] font-semibold text-stone-300 hover:text-white">
                Close
              </button>
            )}
          </div>
        )}
        {error && <p className="mt-2 rounded-lg bg-red-950/60 px-3 py-2 text-[12px] text-red-200">{error}</p>}
      </div>
    </div>
  );
};

export default RubyTillPayRegisterFields;
